import { useEffect, useState, createContext, useContext } from "react";
import { Link, NavLink, Outlet, useParams } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { NAV_SECTIONS } from "../config/modules";
import { RagDot } from "../components/bits";
import AiCopilot from "../components/AiCopilot";
import { ArrowLeft, LogOut, Sparkles, X, ChevronDown, ChevronRight } from "lucide-react";

const ProjectContext = createContext(null);
export const useProject = () => useContext(ProjectContext);

export default function ProjectLayout() {
  const { projectId } = useParams();
  const { user, logout } = useAuth();
  const [project, setProject] = useState(null);
  const [copilotOpen, setCopilotOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({ "Method Toolkits": true });

  const reload = () => api.get(`/projects/${projectId}`).then((r) => setProject(r.data)).catch(() => {});
  useEffect(() => { reload(); }, [projectId]);

  const enabled = new Set((project?.enabledTools || []).filter((t) => t.isEnabled).map((t) => t.toolCode));

  return (
    <ProjectContext.Provider value={{ project, projectId, reloadProject: reload }}>
      <div className="min-h-screen flex bg-background">
        <aside className="w-64 shrink-0 border-r border-border bg-card flex flex-col h-screen sticky top-0">
          <div className="p-4 border-b border-border">
            <Link to="/projects" className="text-xs text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
              <ArrowLeft className="h-3 w-3" /> All projects
            </Link>
            <div className="font-display font-black tracking-tighter leading-tight mt-1 text-sm" data-testid="sidebar-project-name">
              {project?.name || "…"}
            </div>
            <div className="flex items-center gap-2 mt-1.5">
              <RagDot value={project?.overallRag} />
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{project?.primaryMethodology === "Pmbok" ? "PMBOK" : project?.primaryMethodology}</span>
            </div>
          </div>
          <nav className="flex-1 overflow-y-auto py-2">
            {NAV_SECTIONS.map((section) => {
              const renderItems = (items) => items.map((item) => (
                <NavLink key={item.path} to={`/projects/${projectId}/${item.path}`} data-testid={`nav-${item.path}`}
                  className={({ isActive }) =>
                    `block px-4 py-1.5 text-[13px] border-l-2 ${isActive ? "border-[#1E6FE8] text-[#1E6FE8] bg-[#1E6FE8]/5 font-semibold" : "border-transparent text-neutral-600 hover:text-foreground hover:bg-muted"}`}>
                  {item.label}
                </NavLink>
              ));
              if (section.subs) {
                const open = openGroups[section.label] !== false;
                return (
                  <div key={section.label} className="mb-1">
                    <button data-testid="nav-method-toolkits-toggle"
                      onClick={() => setOpenGroups((g) => ({ ...g, [section.label]: !open }))}
                      className="w-full flex items-center justify-between px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground hover:text-foreground">
                      {section.label}
                      {open ? <ChevronDown className="h-3 w-3" /> : <ChevronRight className="h-3 w-3" />}
                    </button>
                    {open && section.subs.map((sub) => {
                      const items = sub.items.filter((i) => !i.tool || enabled.has(i.tool) || !project);
                      if (items.length === 0) return null;
                      return (
                        <div key={sub.label}>
                          <div className="px-4 pt-1.5 pb-0.5 text-[10px] font-semibold tracking-wide text-[#1E6FE8]/70">{sub.label}</div>
                          {renderItems(items)}
                        </div>
                      );
                    })}
                  </div>
                );
              }
              const items = section.items.filter((i) => !i.tool || enabled.has(i.tool) || !project);
              if (items.length === 0) return null;
              return (
                <div key={section.label} className="mb-1">
                  <div className="px-4 pt-3 pb-1 text-[10px] font-bold uppercase tracking-[0.2em] text-muted-foreground">{section.label}</div>
                  {renderItems(items)}
                </div>
              );
            })}
          </nav>
          <div className="border-t border-border p-3 text-xs flex items-center justify-between">
            <div className="truncate">
              <div className="font-medium truncate">{user?.name}</div>
              <div className="text-muted-foreground truncate">{user?.tenantName}</div>
            </div>
            <button data-testid="logout-btn" onClick={logout} className="p-1.5 hover:text-red-600"><LogOut className="h-4 w-4" /></button>
          </div>
        </aside>

        <main className="flex-1 min-w-0 p-6 overflow-x-auto">
          <Outlet />
        </main>

        {copilotOpen && (
          <aside className="w-[420px] shrink-0 border-l border-border bg-neutral-950 text-neutral-100 h-screen sticky top-0 flex flex-col">
            <div className="flex items-center justify-between px-4 py-3 border-b border-neutral-800">
              <div className="flex items-center gap-2 font-display font-bold tracking-tight text-sm">
                <Sparkles className="h-4 w-4 text-[#5B93F0]" /> AI COPILOT
              </div>
              <button data-testid="close-copilot-btn" onClick={() => setCopilotOpen(false)} className="p-1 hover:text-red-400"><X className="h-4 w-4" /></button>
            </div>
            <AiCopilot projectId={projectId} />
          </aside>
        )}

        {!copilotOpen && (
          <button data-testid="open-copilot-btn" onClick={() => setCopilotOpen(true)}
            className="fixed bottom-6 right-6 z-40 bg-[#1E6FE8] text-white px-4 py-3 font-display font-bold text-sm tracking-tight inline-flex items-center gap-2 hover:bg-[#1557C6] shadow-lg">
            <Sparkles className="h-4 w-4" /> AI COPILOT
          </button>
        )}
      </div>
    </ProjectContext.Provider>
  );
}
